import type {
  EmailTemplateSummary,
  RenderedEmailResponse,
  SendingIdentityResponse,
} from "schemas/bedrockApi/emailTemplates";
import type { EmailTemplateDoc, EmailTemplateType } from "schemas/email";
import type { RequestFn } from "./request";

/** Client for the email builder's internal routes (`/api/v1/email-templates/*`); templates are project-scoped and keyed by type. */
export function createEmailTemplatesClient({
  request,
}: {
  request: RequestFn;
}) {
  return {
    list: (input: { projectId: string }) => {
      return request<EmailTemplateSummary[]>({
        method: "GET",
        path: "/api/v1/email-templates",
        query: { projectId: input.projectId },
      });
    },
    getOrCreate: (input: { projectId: string; type: EmailTemplateType }) => {
      return request<EmailTemplateDoc>({
        method: "POST",
        path: "/api/v1/email-templates/get-or-create",
        body: input,
      });
    },
    sourceStatus: (input: { projectId: string; type: EmailTemplateType }) => {
      return request<{ stale: boolean }>({
        method: "GET",
        path: `/api/v1/email-templates/${encodeURIComponent(
          input.type,
        )}/source-status`,
        query: { projectId: input.projectId },
      });
    },
    sendingIdentity: (input: { projectId: string }) => {
      return request<SendingIdentityResponse>({
        method: "GET",
        path: "/api/v1/email-templates/sending-identity",
        query: { projectId: input.projectId },
      });
    },
    saveCompiledDraft: (input: {
      projectId: string;
      type: EmailTemplateType;
      subject: string;
      preheader: string;
      bodyHtml: string;
      sourceTsx: string;
    }) => {
      return request<EmailTemplateDoc>({
        method: "PUT",
        path: "/api/v1/email-templates/draft",
        body: input,
      });
    },
    renderPreview: (input: {
      projectId: string;
      type: EmailTemplateType;
      artifact: "draft" | "live";
    }) => {
      return request<RenderedEmailResponse>({
        method: "POST",
        path: "/api/v1/email-templates/render-preview",
        body: input,
      });
    },
    sendTest: (input: {
      projectId: string;
      type: EmailTemplateType;
      artifact: "draft" | "live";
      to: string;
      variables?: Record<string, string>;
    }) => {
      return request<{ id: string }>({
        method: "POST",
        path: "/api/v1/email-templates/send-test",
        body: input,
      });
    },
    publish: (input: { projectId: string; type: EmailTemplateType }) => {
      return request<EmailTemplateSummary>({
        method: "POST",
        path: "/api/v1/email-templates/publish",
        body: input,
      });
    },
    rollback: (input: { projectId: string; type: EmailTemplateType }) => {
      return request<EmailTemplateSummary>({
        method: "POST",
        path: "/api/v1/email-templates/rollback",
        body: input,
      });
    },
  };
}

export type EmailTemplatesClient = ReturnType<
  typeof createEmailTemplatesClient
>;
